// Journey step statuses, shared by the JourneyRail dots and the landing pick.
// The derivation is pure (unit-tested); useStepStatuses wires it to the
// same React Query caches the step views already populate, so reading statuses
// from any surface is free.

import { useProject } from '@/hooks/use-projects';
import { useDialogs } from '@/hooks/use-dialog';
import { useCanvasData } from '@/hooks/use-canvas-data';
import { useMarketResearchStatus } from '@/hooks/use-market-research';
import { useSynthesis } from '@/hooks/use-synthesis';
import { useCapabilities } from '@/hooks/use-capabilities';
import { useDeck } from '@/hooks/use-deck';
import { isFramingCurrent } from '@/features/idea/framing-state';
import type { NodeMetadata } from '@/services/canvas-mappers';

export type StepStatus = 'not-started' | 'in-progress' | 'done';

export type StepId =
  | 'idea'
  | 'market'
  | 'assumptions'
  | 'dialog'
  | 'synthesis'
  | 'capabilities'
  | 'thesis'
  | 'deck';

export interface StepStatusInputs {
  project: {
    idea?: string | null;
    aspiration?: string | null;
    customerSegment?: { role?: string | null } | null;
    respondentFraming?: string | null;
    openerBody?: string | null;
    framingSourceAspiration?: string | null;
    marketReviewed?: boolean;
    assumptionsReviewed?: boolean;
    dialogsReviewed?: boolean;
    synthesisReviewed?: boolean;
    capabilitiesReviewed?: boolean;
    thesisReviewed?: boolean;
  };
  nodes: Array<{ type?: string; metadataSource?: string }>;
  dialogCount: number;
  marketRunning: boolean;
  hasSynthesisProposal: boolean;
  hasCapabilitiesProposal: boolean;
  hasDeck: boolean;
}

export function deriveStepStatuses(inputs: StepStatusInputs): Record<StepId, StepStatus> {
  const {
    project,
    nodes,
    dialogCount,
    marketRunning,
    hasSynthesisProposal,
    hasCapabilitiesProposal,
    hasDeck,
  } = inputs;

  const ideaFilledCount = [
    project.idea?.trim(),
    project.aspiration?.trim(),
    project.customerSegment?.role?.trim(),
  ].filter(Boolean).length;
  const idea: StepStatus =
    ideaFilledCount === 3 && isFramingCurrent(project)
      ? 'done'
      : ideaFilledCount > 0
        ? 'in-progress'
        : 'not-started';

  const marketSignalCount = nodes.filter((n) => n.type === 'market_signal').length;
  const market: StepStatus = project.marketReviewed
    ? 'done'
    : marketRunning || marketSignalCount > 0
    ? 'in-progress'
    : 'not-started';

  const assumptionCount = nodes.filter((n) => n.type === 'assumption').length;
  const assumptions: StepStatus = project.assumptionsReviewed
    ? 'done'
    : assumptionCount > 0
    ? 'in-progress'
    : 'not-started';

  // "Done" for dialogs = "enough dialogs for this iteration" — a PM judgment
  // (like the other reviewed flags), not a respondent-count inference.
  const dialog: StepStatus = project.dialogsReviewed
    ? 'done'
    : dialogCount > 0
    ? 'in-progress'
    : 'not-started';

  const synthesisedNodeCount = nodes.filter((n) => n.metadataSource === 'synthesis').length;
  const synthesis: StepStatus = project.synthesisReviewed
    ? 'done'
    : synthesisedNodeCount > 0 || hasSynthesisProposal
    ? 'in-progress'
    : 'not-started';

  const capabilityCount = nodes.filter((n) => n.type === 'capability').length;
  const capabilities: StepStatus = project.capabilitiesReviewed
    ? 'done'
    : capabilityCount > 0 || hasCapabilitiesProposal
    ? 'in-progress'
    : 'not-started';

  // Step 7 is actionable once there's anything for the report to reason about —
  // capabilities to map, synthesised needs that may be unaddressed, or
  // assumptions on the idea.
  const thesis: StepStatus = project.thesisReviewed
    ? 'done'
    : capabilityCount > 0 || synthesisedNodeCount > 0 || assumptionCount > 0
    ? 'in-progress'
    : 'not-started';

  // Step 8 is "done" once a deck has been generated (DECK#LATEST exists);
  // regenerate keeps it done. Terminal generate, so no partial state.
  const deck: StepStatus = hasDeck ? 'done' : 'not-started';

  return { idea, market, assumptions, dialog, synthesis, capabilities, thesis, deck };
}

// A phase groups the sequential steps that share a goal (e.g. Assess = market
// research + assumptions).
export function phaseStatus(statuses: StepStatus[]): StepStatus {
  if (statuses.every((s) => s === 'done')) return 'done';
  if (statuses.some((s) => s !== 'not-started')) return 'in-progress';
  return 'not-started';
}

// All the queries here are cached reads shared with the step views/journey
// cards (same keys), so mounting this from the rail on every step view adds no
// new fetches. Returns null until the project is loaded.
export function useStepStatuses(projectId: string): Record<StepId, StepStatus> | null {
  const { data: project } = useProject(projectId);
  const { data: dialogs = [] } = useDialogs(projectId);
  const { data: canvas } = useCanvasData(projectId);
  const marketStatusQuery = useMarketResearchStatus(projectId);
  const { data: synthesisProposal } = useSynthesis(projectId);
  const { data: capabilitiesProposal } = useCapabilities(projectId);
  const { data: deck } = useDeck(projectId);

  if (!project) return null;

  return deriveStepStatuses({
    project,
    nodes: (canvas?.nodes ?? []).map((n) => ({
      type: n.type,
      metadataSource: (n.data?.metadata as NodeMetadata | undefined)?.source,
    })),
    dialogCount: dialogs.length,
    marketRunning: marketStatusQuery.data?.status === 'running',
    hasSynthesisProposal: !!synthesisProposal,
    hasCapabilitiesProposal: !!capabilitiesProposal,
    hasDeck: !!deck,
  });
}
